import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { PointsAccountFacade } from '@features/points-account/facades/points-account/points-account.facade';
import { UserPreferenceFacade } from '@features/user-preference/facades/user-preference/user-preference.facade';
import { DateService } from '@rd/core/services/date/date.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { Enrollment, LoyaltyCurrency, PointsAccount, Tranche } from '@rd/types';
import { map, take } from 'rxjs';

@Component({
  selector: 'anz-card-select',
  templateUrl: './card-select.component.html',
  styleUrls: ['./card-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardSelectComponent implements OnDestroy {
  @HostBinding('class.anz-card-select') componentClass = true;
  @Input() pointsAccount: PointsAccount;
  @Input() loyaltyCurrency: LoyaltyCurrency;

  constructor(
    private urlUtilsService: UrlUtilsService,
    private pointsAccountFacade: PointsAccountFacade,
    private userPreferenceFacade: UserPreferenceFacade,
    private router: Router,
    private dateService: DateService,
    public elementRef: ElementRef,
  ) {}

  get enrollment(): Enrollment {
    return this.pointsAccount.enrollments[0];
  }

  get imgUrl(): string {
    const supportedImages = new Map([
      ['301', '301'],
      ['302', '301'], // 302 uses the same image as 301
      ['304', '304'],
      ['308', '308'],
      ['312', '312'],
    ]);
    const code =
      supportedImages.get(this.enrollment.productCode) ?? 'anz_unavailablecard';
    return this.urlUtilsService.imageUrl(`/images/${code}.png`);
  }

  get cardLastFour(): string {
    const cardNumber = this.pointsAccount.enrollments[0].groupReferenceId;
    return cardNumber.slice(cardNumber.length - 4);
  }

  get expiringPointsNumber(): number {
    const dayjs = this.dateService.dayjs;
    const endOfYear = dayjs().endOf('year');
    const tranches = this.pointsAccount.tranches;
    return tranches
      .filter((tranche: Tranche) =>
        endOfYear.isSame(dayjs(tranche.expiryDate), 'day'),
      )
      .reduce((total, curr) => {
        return curr.balance + total;
      }, 0);
  }

  get lastDayOfYear(): string {
    return this.dateService.dayjs().endOf('year').format('DD/MM/YYYY');
  }

  ngOnDestroy(): void {
    this.pointsAccountFacade.triggerEffectsAfterPointsAccountSelection();
  }

  handleClick(pointsAccount: PointsAccount): void {
    this.userPreferenceFacade.userPreferences$
      .pipe(
        map((userPreference) => userPreference.pointsAccountId),
        take(1),
      )
      .subscribe((pointsAccountId) => {
        if (pointsAccountId !== pointsAccount.id) {
          this.userPreferenceFacade.updatePointsAccountId(pointsAccount.id);
        }
        this.router.navigateByUrl('/');
      });
  }
}
